import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { CollisionWorld } from '../world/CollisionWorld';
import { hitscan, type HitTarget } from './Hitscan';

export interface AimSolution {
  /** World point the shot starts from. */
  origin: Vector3;
  /** Normalised shot direction. */
  direction: Vector3;
  /** World point the shot converges on. */
  point: Vector3;
}

const scratchCameraRay = new Vector3();

/** How many steps the origin is walked back before giving up and using `anchor`. */
const ORIGIN_STEPS = 8;
/** The closest range the lean lock will divide by, metres. */
const LEAN_LOCK_MIN_RANGE = 0.5;

/** A look correction, in radians, that re-aims after the eye has moved. */
export interface LeanLock {
  yaw: number;
  pitch: number;
}

/**
 * The look correction that keeps the aim *on the target* while the eye leans.
 *
 * The Matrix dodge moves the body — and so the eye — out of the bolt path, but
 * the player is still aiming at the drone. The off-axis window shifts the
 * projection for the eye's new position; without this, the aim point slides
 * with the lean and the picture reads as a camera pan. This is the opposite
 * rotation: the angle the lean subtends at the target's range, so the ray from
 * the moved eye lands back on the same world point.
 *
 * `lateral` and `vertical` are the applied viewer offset in camera-local
 * metres (positive right and up); `range` is the distance to the aim point.
 */
export function leanLock(lateral: number, vertical: number, range: number): LeanLock {
  const distance = Math.max(LEAN_LOCK_MIN_RANGE, range);
  // The eye moved right, so the target is now to the left: turn left, and down
  // for an upward move, by the angle it subtends at that range. The pitch is
  // measured against the yawed horizontal range, so the two compose exactly.
  return {
    yaw: Math.atan2(-lateral, distance),
    pitch: Math.atan2(-vertical, Math.hypot(lateral, distance)),
  };
}

/**
 * Force a shot's origin back inside the room, in the body's own zone.
 *
 * The third-person camera hangs behind the body with no collision of its own,
 * so backing into a wall pulls it *through* that wall (and, on a multi-zone
 * deck, into the next room). A ray fired from there starts on the far side of
 * the wall, and `CollisionWorld.raycast` never sees a plane behind its origin —
 * so the bullet sails straight through the wall the player is looking at. The
 * origin is walked from the camera toward `anchor` until it is back inside the
 * body's zone; a camera already inside is returned untouched.
 */
export function clampShotOrigin(
  world: CollisionWorld,
  point: Vector3,
  anchor: Vector3,
  out: Vector3,
): void {
  if (world.sameZone(point, anchor) && world.isInsideRoom(point)) {
    out.copy(point);
    return;
  }
  for (let step = 1; step <= ORIGIN_STEPS; step += 1) {
    out.lerpVectors(point, anchor, step / ORIGIN_STEPS);
    if (world.sameZone(out, anchor) && world.isInsideRoom(out)) return;
  }
  // Nothing on the line was inside: the anchor is the last resort, and the
  // body it belongs to is the one thing guaranteed to be in the room.
  out.copy(anchor);
}

/**
 * Resolve the third-person shot.
 *
 * The head ray anchors the world-space reticle. The camera ray to that anchor
 * decides what the player actually sees under the reticle, and it is also the
 * shot ray. Using the camera ray here is deliberate: the camera sits several
 * metres behind the muzzle, so a muzzle-to-reticle ray can clip a doorway or
 * wall that the player can clearly see past. The tracer still starts at the
 * muzzle, so the visible gun reads correctly.
 */
export function resolveThirdPersonAim(
  cameraPosition: Vector3,
  cameraForward: Vector3,
  headPosition: Vector3,
  headDirection: Vector3,
  world: CollisionWorld,
  targets: readonly HitTarget[],
  out: AimSolution,
): AimSolution {
  const headHit = world.raycast(headPosition, headDirection, BALANCE.combat.range);
  if (headHit) {
    out.point.copy(headHit.point);
  } else {
    out.point.copy(headPosition).addScaledVector(headDirection, BALANCE.combat.range);
  }

  // A camera that has been pushed through a wall cannot be trusted to resolve
  // the shot: the wall it is behind is invisible to a ray starting outside. In
  // that case the head ray, which starts inside the room, stands.
  const cameraUsable =
    world.sameZone(cameraPosition, headPosition) && world.isInsideRoom(cameraPosition);

  // Resolve the point under the reticle from the camera. A point behind the
  // camera cannot be aimed at, so the head-ray result is kept in that case.
  scratchCameraRay.copy(out.point).sub(cameraPosition);
  if (cameraUsable && scratchCameraRay.dot(cameraForward) > 0) {
    scratchCameraRay.normalize();
    const shot = hitscan(cameraPosition, scratchCameraRay, BALANCE.combat.range, world, targets);
    out.point.copy(shot.point);
  }

  // Third person fires the camera ray itself. This is what lets a shot pass
  // through a doorway the player can see through, instead of being stopped by
  // the muzzle's parallax against the wall beside it. The origin is clamped
  // back into the body's zone first, so a wall-clipped camera cannot fire
  // through the wall behind it.
  clampShotOrigin(world, cameraPosition, headPosition, out.origin);
  out.direction.copy(out.point).sub(out.origin);
  if (out.direction.lengthSq() > 1e-8) out.direction.normalize();
  return out;
}
